import { useState } from 'react';
import { useAuth as useAuthContext } from '../context/AuthContext';
import { authService } from '../services/api';
import { LoginRequest, RegisterRequest, TwoFAValidateRequest, User } from '../types';

// Type pour le résultat de la validation 2FA
type TwoFAValidationResult = 
  | { success: true; message?: string }
  | { success: false; error: string };

export const useAuth = () => {
  const { login: setAuth, logout: clearAuth, user, token, isAuthenticated, updateUser: updateContextUser } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requires2FA, setRequires2FA] = useState(false);
  
  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.login(credentials);
      
      // Si la réponse indique que 2FA est requis et qu'aucun code OTP n'a été fourni
      if (response.requires2FA === true && !credentials.otpCode) {
        console.log('2FA required in useAuth hook');
        setRequires2FA(true);
        setIsLoading(false);
        return { requires2FA: true };
      }
      
      // Sinon, connexion réussie
      setAuth(response);
      setRequires2FA(false);
      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.message || 'Une erreur est survenue lors de la connexion';
      setError(message);
      setIsLoading(false);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.register(data);
      setAuth(response);
      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.message || 'Une erreur est survenue lors de l\'inscription';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuth();
  };

  const setup2FA = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.setup2FA();
      return { success: true, data: response };
    } catch (err: any) {
      const message = err.response?.data?.message || 'Une erreur est survenue lors de la configuration 2FA';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const validate2FA = async (data: TwoFAValidateRequest): Promise<TwoFAValidationResult> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.validate2FA(data);
      
      if (response.success) {
        // Mettre à jour l'utilisateur dans le contexte pour refléter l'activation de 2FA
        if (user) {
          const updatedUser = { ...user, twoFA: true };
          updateContextUser(updatedUser);
        }
        
        return { 
          success: true,
          message: response.message
        };
      } else {
        const errorMsg = response.error || 'Code OTP invalide';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err: any) {
      console.error('Erreur lors de la validation 2FA:', err);
      const message = err.response?.data?.error || 'Erreur lors de la validation du code';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FA = async (): Promise<TwoFAValidationResult> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.disable2FA();
      if (response.success) {
        // Mettre à jour l'utilisateur dans le contexte pour refléter la désactivation de 2FA
        if (user) {
          const updatedUser = { ...user, twoFA: false };
          updateContextUser(updatedUser);
        }
        
        return { 
          success: true,
          message: response.message || 'Authentification à deux facteurs désactivée avec succès'
        };
      } else {
        const errorMsg = response.error || 'Erreur lors de la désactivation de la 2FA';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err: any) {
      const message = err.response?.data?.error || 'Erreur lors de la désactivation de la 2FA';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (user: User) => {
    updateContextUser(user);
  };

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    requires2FA,
    login,
    logout,
    register,
    setup2FA,
    validate2FA,
    disable2FA,
    updateUser
  };
};

export default useAuth;
